import React from "react";
import { StarRating } from "@/components/common";
import { REVIEWS, SITE } from "@/mock";

const GoogleWord = ({ className = "text-xl font-bold" }) => (
  <span className={className} aria-label="Google">
    <span style={{ color: "#4285F4" }}>G</span>
    <span style={{ color: "#EA4335" }}>o</span>
    <span style={{ color: "#FBBC05" }}>o</span>
    <span style={{ color: "#4285F4" }}>g</span>
    <span style={{ color: "#34A853" }}>l</span>
    <span style={{ color: "#EA4335" }}>e</span>
  </span>
);

function ReviewCard({ review }) {
  return (
    <figure className="w-[260px] shrink-0 rounded-2xl bg-white p-5 text-left shadow-sm ring-1 ring-neutral-100 sm:w-[300px]" data-testid="review-card">
      <div className="flex items-center justify-between">
        <StarRating value={review.rating} size={15} />
        <GoogleWord className="text-sm font-bold" />
      </div>
      <blockquote className="mt-2.5 line-clamp-3 text-sm leading-relaxed text-neutral-700">
        &ldquo;{review.text}&rdquo;
      </blockquote>
      <figcaption className="mt-3 flex items-center gap-3 border-t border-neutral-100 pt-3">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#D0021B] text-sm font-bold text-white">
          {review.name.charAt(0)}
        </span>
        <span>
          <span className="block text-sm font-bold text-neutral-900">{review.name}</span>
          <span className="block text-xs text-neutral-500">{review.date} · via Google</span>
        </span>
      </figcaption>
    </figure>
  );
}

/**
 * ReviewsCarousel - Google reviews shown as a continuous right-to-left marquee.
 * No arrows/dots, no crossfade flashing. Two identical halves loop seamlessly and pause on hover.
 * A clickable Google badge + button link straight to the store's Google reviews.
 */
export default function ReviewsCarousel({ reviews = REVIEWS }) {
  return (
    <div data-testid="reviews-carousel">
      {/* Clickable Google rating badge */}
      <div className="flex justify-center">
        <a
          href={SITE.googleReviewUrl}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="google-reviews-badge"
          className="inline-flex items-center gap-3 rounded-full border border-neutral-200 bg-white px-5 py-2.5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
        >
          <GoogleWord />
          <StarRating value={5} size={18} />
          <span className="text-sm font-bold text-neutral-900">{SITE.rating.toFixed(1)}</span>
          <span className="text-xs text-neutral-500">· {SITE.reviewCount}+ Google Reviews</span>
        </a>
      </div>

      {/* Continuous marquee of review cards (two identical halves for a seamless loop) */}
      <div className="relative mt-6 overflow-hidden">
        <div className="reviews-track flex w-max">
          {[0, 1].map((half) => (
            <div key={half} className="flex gap-5 pr-5" aria-hidden={half === 1}>
              {reviews.map((r, i) => (
                <ReviewCard key={`${half}-${r.name}-${i}`} review={r} />
              ))}
            </div>
          ))}
        </div>
        {/* Soft edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-10 bg-gradient-to-r from-neutral-100 to-transparent sm:w-16" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-10 bg-gradient-to-l from-neutral-100 to-transparent sm:w-16" />
      </div>
    </div>
  );
}
